export const BCR_FORMULAS = [
  { id: "freq", label: "Tần suất" },
  { id: "bet", label: "Theo cầu bệt" },
  { id: "chop", label: "Cầu 1-1" },
  { id: "bigroad", label: "Cầu Big Road" },
] as const;

export type BcrFormulaId = (typeof BCR_FORMULAS)[number]["id"];
export type BcrSide = "P" | "B";

export type BcrFormulaAnalysis = {
  formula: BcrFormulaId;
  winner: BcrSide;
  forecast: number;
  hint: string;
  advice: string;
  buttonNote: string;
  streakLen: number;
  streakSide: BcrSide | null;
};

export type BcrBead = BcrSide | "T";

export function recentRoadPreview(tableData: any[], limit = 10): BcrBead[] {
  return sortedRounds(tableData)
    .map((item) => roadToSide(item?.road))
    .slice(-limit);
}

export function analyzeAllBaccaratFormulas(tableData: any[]): BcrFormulaAnalysis[] {
  return BCR_FORMULAS.map((item) => analyzeBaccaratFormula(tableData, item.id));
}

function roadToSide(road: unknown): BcrSide | "T" {
  const n = Number(road);
  if ([0, 1, 2].includes(n)) return "B";
  if ([8, 9, 10].includes(n)) return "P";
  return "T";
}

function sideLabel(side: BcrSide): string {
  return side === "P" ? "Người chơi (P)" : "Nhà cái (B)";
}

function clampForecast(value: number): number {
  return Math.max(52, Math.min(94, Math.round(value)));
}

function sortedRounds(tableData: any[]): any[] {
  return [...(Array.isArray(tableData) ? tableData : [])].sort(
    (a, b) => (a?.stampTime ?? 0) - (b?.stampTime ?? 0)
  );
}

function nonTieSides(rounds: any[]): BcrSide[] {
  return rounds
    .map((item) => roadToSide(item?.road))
    .filter((side): side is BcrSide => side === "P" || side === "B");
}

function lastStreak(sides: BcrSide[]): { side: BcrSide | null; len: number } {
  if (sides.length === 0) return { side: null, len: 0 };
  const side = sides[sides.length - 1] as BcrSide;
  let len = 0;
  for (let i = sides.length - 1; i >= 0; i -= 1) {
    if (sides[i] !== side) break;
    len += 1;
  }
  return { side, len };
}

function frequency(sides: BcrSide[]): { winner: BcrSide; player: number; banker: number } {
  const recent = sides.slice(-12);
  const player = recent.filter((side) => side === "P").length;
  const banker = recent.filter((side) => side === "B").length;
  return {
    winner: player === banker ? (recent[recent.length - 1] ?? "P") : player > banker ? "P" : "B",
    player,
    banker,
  };
}

function chopScore(sides: BcrSide[]): number {
  const recent = sides.slice(-8);
  if (recent.length < 4) return 0;
  let alt = 0;
  for (let i = 1; i < recent.length; i += 1) {
    if (recent[i] !== recent[i - 1]) alt += 1;
  }
  return alt / (recent.length - 1);
}

function bigRoadPrediction(rounds: any[], sides: BcrSide[]): BcrSide {
  const withX = rounds.filter((item) => typeof item?.showX === "number");
  if (withX.length > 0) {
    const last = withX[withX.length - 1];
    const col = withX.filter((item) => item.showX === last.showX);
    const colSides = nonTieSides(col);
    if (colSides.length >= 2) return colSides[colSides.length - 1] as BcrSide;
    if (colSides.length === 1) {
      const prevCols = withX.filter((item) => item.showX < last.showX);
      const prevSides = nonTieSides(prevCols);
      if (prevSides.length > 0) return prevSides[prevSides.length - 1] === "P" ? "B" : "P";
    }
  }
  const streak = lastStreak(sides);
  if (streak.len >= 2 && streak.side) return streak.side;
  const last = sides[sides.length - 1];
  return last === "P" ? "B" : "P";
}

export function analyzeBaccaratFormula(
  tableData: any[],
  formula: BcrFormulaId
): BcrFormulaAnalysis {
  const rounds = sortedRounds(tableData);
  const sides = nonTieSides(rounds);
  const streak = lastStreak(sides);
  const freq = frequency(sides);
  const fallback: BcrSide = freq.winner;

  if (sides.length === 0) {
    return {
      formula,
      winner: "P",
      forecast: 55,
      streakLen: 0,
      streakSide: null,
      hint: "Chưa đủ cầu để đọc. Đợi thêm kết quả.",
      advice: "Bàn chưa có cầu — giữ quan sát, chưa nên vào tay.",
      buttonNote: "Chưa có cầu",
    };
  }

  if (formula === "freq") {
    const total = Math.max(1, freq.player + freq.banker);
    const share = (fallback === "P" ? freq.player : freq.banker) / total;
    return {
      formula,
      winner: fallback,
      forecast: clampForecast(52 + share * 42),
      streakLen: streak.len,
      streakSide: streak.side,
      hint: `12 ván gần: P ${freq.player} — B ${freq.banker}. Chọn bên nhiều hơn.`,
      advice: `Tần suất ${total} ván gần nhất: Người chơi ${freq.player} — Nhà cái ${freq.banker}. Nghiêng ${sideLabel(fallback)}, nên theo tay này.`,
      buttonNote: `Nghiêng ${fallback} (${freq.player}-${freq.banker})`,
    };
  }

  if (formula === "bet") {
    if (streak.len >= 3 && streak.side) {
      return {
        formula,
        winner: streak.side,
        forecast: clampForecast(64 + streak.len * 4),
        streakLen: streak.len,
        streakSide: streak.side,
        hint: `Đang bệt ${streak.len} tay ${sideLabel(streak.side)} — theo bệt.`,
        advice: `Đang bệt ${streak.len} tay ${sideLabel(streak.side)} — theo cầu, đánh tiếp ${sideLabel(streak.side)}.`,
        buttonNote: `Bệt ${streak.len} ${streak.side}`,
      };
    }
    if (streak.len === 2 && streak.side) {
      return {
        formula,
        winner: streak.side,
        forecast: clampForecast(61),
        streakLen: streak.len,
        streakSide: streak.side,
        hint: `Mới 2 tay ${sideLabel(streak.side)} — cầu bệt chưa chắc.`,
        advice: `Mới 2 tay ${sideLabel(streak.side)} — cầu bệt chưa chắc, tham khảo theo ${sideLabel(streak.side)}.`,
        buttonNote: `2 tay ${streak.side}`,
      };
    }
    const winner = streak.side ?? fallback;
    return {
      formula,
      winner,
      forecast: clampForecast(54),
      streakLen: streak.len,
      streakSide: streak.side,
      hint: streak.len === 1
        ? `Vừa 1 tay ${sideLabel(winner)} — chưa thành bệt.`
        : "Chưa thành bệt — tín hiệu yếu.",
      advice: streak.len === 1
        ? `Chưa thành bệt (mới 1 tay ${sideLabel(winner)}) — tín hiệu yếu, chỉ tham khảo.`
        : `Chưa thành bệt — tín hiệu yếu, dự đoán tham khảo ${sideLabel(winner)}.`,
      buttonNote: "Chưa bệt",
    };
  }

  if (formula === "chop") {
    const score = chopScore(sides);
    const last = sides[sides.length - 1] as BcrSide;
    const opposite: BcrSide = last === "P" ? "B" : "P";
    if (score >= 0.7) {
      return {
        formula,
        winner: opposite,
        forecast: clampForecast(58 + score * 30),
        streakLen: streak.len,
        streakSide: streak.side,
        hint: `Cầu 1-1 đang đẹp — ván trước ${sideLabel(last)}, bẻ ${sideLabel(opposite)}.`,
        advice: `Cầu 1-1 đang đẹp — ván trước ${sideLabel(last)}, đánh bẻ ${sideLabel(opposite)}.`,
        buttonNote: `1-1 đẹp → ${opposite}`,
      };
    }
    return {
      formula,
      winner: fallback,
      forecast: clampForecast(56),
      streakLen: streak.len,
      streakSide: streak.side,
      hint: `Cầu 1-1 đang gãy — nghiêng tần suất ${sideLabel(fallback)}.`,
      advice: `Cầu 1-1 đang gãy — không nên ping-pong. Nghiêng tần suất ${sideLabel(fallback)}.`,
      buttonNote: `1-1 gãy → ${fallback}`,
    };
  }

  const winner = bigRoadPrediction(rounds, sides);
  const followColumn = streak.len >= 2;
  return {
    formula,
    winner,
    forecast: clampForecast(followColumn ? 66 + streak.len * 3 : 60),
    streakLen: streak.len,
    streakSide: streak.side,
    hint: followColumn
      ? `Cột Big Road đang chạy ${sideLabel(winner)}.`
      : `Cột Big Road vừa chuyển — ${sideLabel(winner)}.`,
    advice: followColumn
      ? `Cột Big Road đang chạy ${sideLabel(winner)} — theo cột, đánh ${sideLabel(winner)}.`
      : `Cột Big Road vừa chuyển — dự đoán ${sideLabel(winner)}, quan sát thêm 1 tay.`,
    buttonNote: followColumn ? `Cột chạy ${winner}` : `Cột mới ${winner}`,
  };
}
